import { useMemo, useState } from 'react';
import { PROFIT_PRODUCTS, PROFIT_CURRENCIES, LOT_QUICK } from '@/mocks/profit';
import {
  formatVN,
  formatSigned,
  Label,
  NumberInput,
  SelectInput,
  QuickChips,
  Toggle,
  Stat,
  Slider,
} from '../ui';

const PROFIT_COLOR = '#10b981';
const LOSS_COLOR = '#fb7185';

const WHEN_CARDS = [
  {
    step: '01',
    icon: 'ri-arrow-go-forward-line',
    title: 'Trước khi vào lệnh',
    desc: 'Ước tính kết quả ở nhiều mức giá khác nhau trước khi mở vị thế để đảm bảo an toàn vốn.',
    tag: 'Chuẩn bị',
    featured: false,
  },
  {
    step: '02',
    icon: 'ri-radar-line',
    title: 'Trong khi theo dõi giao dịch',
    desc: 'Thay đổi giá giả định để xem lợi nhuận hoặc thua lỗ thay đổi linh hoạt theo thị trường.',
    tag: 'Giai đoạn vàng',
    featured: true,
  },
  {
    step: '03',
    icon: 'ri-check-double-line',
    title: 'Sau khi giao dịch',
    desc: 'Kiểm tra kết quả thực tế dựa trên giá vào và giá đóng lệnh để đúc rút kinh nghiệm.',
    tag: 'Đúc rút',
    featured: false,
  },
];

export default function ProfitTab({ onNext }: { onNext: () => void }) {
  const [product, setProduct] = useState('EURUSD');
  const [direction, setDirection] = useState('buy');
  const [openPrice, setOpenPrice] = useState(1.085);
  const [lots, setLots] = useState(1);
  const [currency, setCurrency] = useState('USD');
  const [balance, setBalance] = useState(10000);
  const [mode, setMode] = useState('price');
  const [closePrice, setClosePrice] = useState(1.09);
  const [targetProfit, setTargetProfit] = useState(500);

  const prod = useMemo(() => PROFIT_PRODUCTS.find((p) => p.value === product) ?? PROFIT_PRODUCTS[0], [product]);
  const curr = useMemo(() => PROFIT_CURRENCIES.find((c) => c.value === currency) ?? PROFIT_CURRENCIES[0], [currency]);

  const sliderMin = prod.price - 50 * prod.pipSize;
  const sliderMax = prod.price + 100 * prod.pipSize;

  const selectProduct = (v: string) => {
    setProduct(v);
    const next = PROFIT_PRODUCTS.find((p) => p.value === v) ?? PROFIT_PRODUCTS[0];
    setOpenPrice(next.price);
    setClosePrice(next.price + 50 * next.pipSize);
  };

  const pipValueAccount = prod.pipValue * curr.rate;

  const computePnl = (close: number) => {
    const pips = (close - openPrice) / prod.pipSize;
    const gross = pips * pipValueAccount * lots * (direction === 'buy' ? 1 : -1);
    const spread = prod.spreadPips * pipValueAccount * lots;
    const commission = prod.commissionPerLot * lots * curr.rate;
    const swap = prod.swapPerLot * lots * curr.rate;
    const net = gross - spread - commission - swap;
    return { pips, gross, spread, commission, swap, net };
  };

  const result = useMemo(() => computePnl(closePrice), [closePrice, openPrice, lots, direction, prod, curr]);

  // Reverse mode: profit → target price
  const reverse = useMemo(() => {
    const spread = prod.spreadPips * pipValueAccount * lots;
    const commission = prod.commissionPerLot * lots * curr.rate;
    const swap = prod.swapPerLot * lots * curr.rate;
    const costs = spread + commission + swap;
    const grossNeeded = targetProfit + costs;
    const pips = grossNeeded / (pipValueAccount * lots);
    const targetClose = openPrice + (direction === 'buy' ? pips : -pips) * prod.pipSize;
    return { targetClose, costs };
  }, [targetProfit, openPrice, lots, direction, prod, curr]);

  const profit = result.net >= 0;
  const accent = profit ? PROFIT_COLOR : LOSS_COLOR;
  const ratioOnBalance = balance > 0 ? (result.net / balance) * 100 : 0;

  const displayClose = mode === 'price' ? closePrice : reverse.targetClose;
  const displayPnl = mode === 'price' ? result : { ...computePnl(reverse.targetClose), pips: (reverse.targetClose - openPrice) / prod.pipSize };

  return (
    <div className="space-y-6">
      {/* Mode switch */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <i className="ri-question-line" />
            </span>
            <span className="text-sm font-semibold text-slate-700">Bạn muốn tính gì?</span>
          </div>
          <Toggle
            value={mode}
            onChange={setMode}
            options={[
              { value: 'price', label: 'Giá → Lợi nhuận' },
              { value: 'profit', label: 'Lợi nhuận → Giá mục tiêu' },
            ]}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: inputs + slider */}
        <div className="space-y-6 lg:col-span-3">
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <i className="ri-settings-3-line text-lg" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold leading-tight text-slate-900">Thông tin giao dịch</h3>
                <p className="text-xs text-slate-400">Kết quả cập nhật tức thì khi bạn thay đổi</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label>Sản phẩm giao dịch</Label>
                <SelectInput value={product} onChange={selectProduct} options={PROFIT_PRODUCTS.map((p) => ({ value: p.value, label: p.label }))} />
              </div>

              <div>
                <Label>Loại giao dịch</Label>
                <div className="flex rounded-xl bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setDirection('buy')}
                    className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                      direction === 'buy' ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Buy
                  </button>
                  <button
                    type="button"
                    onClick={() => setDirection('sell')}
                    className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                      direction === 'sell' ? 'bg-rose-500 text-white' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Sell
                  </button>
                </div>
              </div>

              {mode === 'profit' && (
                <div>
                  <Label hint={`= ${formatSigned(targetProfit, curr.value, 2)}`}>Mục tiêu lợi nhuận</Label>
                  <NumberInput value={targetProfit} onChange={setTargetProfit} suffix={curr.value} min={0} step={10} />
                </div>
              )}

              <div>
                <Label>Giá vào lệnh</Label>
                <NumberInput value={openPrice} onChange={setOpenPrice} min={0} step={prod.pipSize} />
              </div>

              <div>
                <Label hint={`${formatVN(lots, 2, 2)} lot`}>Khối lượng giao dịch</Label>
                <NumberInput value={lots} onChange={setLots} suffix="lot" min={0.01} step={0.01} />
                <div className="mt-2.5">
                  <QuickChips options={LOT_QUICK} value={lots} onChange={setLots} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label>Tiền tệ tài khoản</Label>
                  <SelectInput value={currency} onChange={setCurrency} options={PROFIT_CURRENCIES.map((c) => ({ value: c.value, label: c.label }))} />
                </div>
                <div>
                  <Label>Số dư tài khoản</Label>
                  <NumberInput value={balance} onChange={setBalance} suffix={curr.value} min={0} step={100} />
                </div>
              </div>
            </div>
          </div>

          {/* Close price slider */}
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-slate-900">Mô phỏng giá đóng lệnh</h3>
              <span className="font-mono text-xs text-slate-400">Giá vào lệnh: {openPrice.toFixed(prod.decimals)}</span>
            </div>

            {mode === 'price' ? (
              <>
                <div className="mb-6 flex items-end justify-between font-mono text-sm text-slate-400">
                  <span>{sliderMin.toFixed(prod.decimals)}</span>
                  <span className="font-mono text-3xl font-bold tabular-nums text-slate-900 md:text-4xl">
                    {closePrice.toFixed(prod.decimals)}
                  </span>
                  <span>{sliderMax.toFixed(prod.decimals)}</span>
                </div>
                <Slider value={closePrice} onChange={setClosePrice} min={sliderMin} max={sliderMax} step={prod.pipSize} accent={accent} />
              </>
            ) : (
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-emerald-600">Giá đóng lệnh mục tiêu</p>
                <p className="mt-2 font-mono text-4xl font-bold tabular-nums text-slate-900">{reverse.targetClose.toFixed(prod.decimals)}</p>
                <p className="mt-1 text-xs text-slate-500">Để đạt lợi nhuận ròng {formatSigned(targetProfit, curr.value, 2)} sau mọi chi phí</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: big result */}
        <div className="lg:col-span-2">
          <div
            className="relative flex h-full flex-col overflow-hidden rounded-3xl border p-6 transition-colors duration-300 md:p-7"
            style={{
              borderColor: `${accent}38`,
              background: `linear-gradient(160deg, ${accent}12 0%, #ffffff 55%)`,
            }}
          >
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl transition-colors duration-500"
              style={{ background: `${accent}24` }}
            />

            <div className="relative">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-500">{profit ? 'Lợi nhuận' : 'Thua lỗ'}</p>
                <span
                  className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold"
                  style={{ backgroundColor: `${accent}1f`, color: accent }}
                >
                  <i className={profit ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
                  {direction === 'buy' ? 'Buy' : 'Sell'} · {prod.label}
                </span>
              </div>

              <div className="mt-6 text-center">
                <p
                  className="font-mono text-5xl font-bold leading-none tabular-nums md:text-6xl"
                  style={{ color: accent, textShadow: `0 0 34px ${accent}55` }}
                >
                  {formatSigned(displayPnl.net, curr.value, 2)}
                </p>
                <p className="mt-3 text-sm text-slate-500">Lợi nhuận ròng ước tính</p>
              </div>

              <div className="mt-7 grid grid-cols-2 gap-2.5">
                <Stat label="Biến động giá" value={`${formatVN(displayPnl.pips, 0, 1)} Pip`} />
                <Stat label="Khối lượng" value={`${formatVN(lots, 2, 2)} Lot`} />
                <Stat label="Tỷ suất trên số dư" value={`${ratioOnBalance >= 0 ? '+' : ''}${formatVN(ratioOnBalance, 1, 1)}%`} accent={accent} />
                <Stat label="Tiền tệ" value={curr.value} />
              </div>

              <div className="mt-7">
                <div className="rounded-2xl border border-slate-200/80 bg-white/70 p-4">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Chi tiết kết quả</p>
                  <div className="mt-3 space-y-2.5">
                    <Row label="Lợi nhuận gộp" value={formatSigned(displayPnl.gross, curr.value, 2)} positive={displayPnl.gross >= 0} />
                    <Row label="Spread" value={formatSigned(-displayPnl.spread, curr.value, 2)} />
                    <Row label="Commission" value={formatSigned(-displayPnl.commission, curr.value, 2)} />
                    <Row label="Swap" value={formatSigned(-displayPnl.swap, curr.value, 2)} />
                    <div className="border-t border-slate-100 pt-2.5">
                      <Row label="Lợi nhuận ròng" value={formatSigned(displayPnl.net, curr.value, 2)} positive={displayPnl.net >= 0} bold />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profit breakdown flow */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <h3 className="font-display text-lg font-bold text-slate-900">Từ lợi nhuận gộp đến lợi nhuận ròng</h3>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <FlowStep label="Lợi nhuận gộp" value={formatSigned(displayPnl.gross, curr.value, 2)} accent={displayPnl.gross >= 0 ? PROFIT_COLOR : LOSS_COLOR} />
          <Arrow />
          <FlowStep label="− Spread" value={formatSigned(-displayPnl.spread, curr.value, 2)} accent="#94a3b8" />
          <Arrow />
          <FlowStep label="− Commission" value={formatSigned(-displayPnl.commission, curr.value, 2)} accent="#94a3b8" />
          <Arrow />
          <FlowStep label="− Swap" value={formatSigned(-displayPnl.swap, curr.value, 2)} accent="#94a3b8" />
          <Arrow />
          <FlowStep label="Lợi nhuận ròng" value={formatSigned(displayPnl.net, curr.value, 2)} accent={accent} strong />
        </div>
      </div>

      {/* When to use */}
      <section>
        <h3 className="font-display text-lg font-bold text-slate-900">Khi nào nên sử dụng công cụ?</h3>

        <div className="mt-5 divide-y divide-slate-100 rounded-2xl border border-slate-200/80 bg-white px-6">
          {WHEN_CARDS.map((c) => (
            <div key={c.title} className="flex items-start gap-4 py-4">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <i className={`${c.icon} text-base`} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{c.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Next step */}
      <NextStep
        title="Bạn đã biết lợi nhuận dự kiến."
        desc="Bước tiếp theo: kiểm tra chi phí giao dịch."
        cta="Tính chi phí Spread"
        onClick={onNext}
      />
    </div>
  );
}

function Row({ label, value, positive, bold }: { label: string; value: string; positive?: boolean; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`${bold ? 'text-sm font-bold text-slate-800' : 'text-xs text-slate-500'}`}>{label}</span>
      <span
        className={`whitespace-nowrap font-mono tabular-nums ${bold ? 'text-base font-bold' : 'text-sm font-semibold'} ${
          positive ? 'text-emerald-600' : 'text-slate-700'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function FlowStep({ label, value, accent, strong }: { label: string; value: string; accent: string; strong?: boolean }) {
  return (
    <div
      className={`rounded-xl border px-4 py-3 text-center ${strong ? 'shadow-sm' : ''}`}
      style={{ borderColor: `${accent}40`, background: `${accent}0f` }}
    >
      <p className="text-[11px] font-medium text-slate-500">{label}</p>
      <p className="mt-1 whitespace-nowrap font-mono text-sm font-bold tabular-nums" style={{ color: accent }}>
        {value}
      </p>
    </div>
  );
}

function Arrow() {
  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-400">
      <i className="ri-arrow-right-line" />
    </span>
  );
}

export function NextStep({ title, desc, cta, onClick }: { title: string; desc: string; cta: string; onClick: () => void }) {
  return (
    <div
      className="rounded-3xl border p-6 md:p-8"
      style={{
        borderColor: 'color-mix(in srgb, var(--tab-accent, #7c3aed) 28%, transparent)',
        background: 'linear-gradient(to bottom right, color-mix(in srgb, var(--tab-accent, #7c3aed) 9%, white), white)',
      }}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-600">{desc}</p>
        </div>
        <button
          type="button"
          onClick={onClick}
          className="group flex items-center justify-center gap-2 whitespace-nowrap rounded-xl px-6 py-3 text-sm font-semibold text-white transition-all hover:brightness-95"
          style={{ backgroundColor: 'var(--tab-accent, #7c3aed)' }}
        >
          {cta}
          <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
}